import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { platformApi } from '@/api/platform';
import { queryKeys } from '@/lib/query-keys';
import type { CreatePortalUserInput, CreateTenantInput, UpdateTenantInput, UUID } from '@/types';

export function usePlatformTenants() {
  return useQuery({
    queryKey: queryKeys.platform.tenants(),
    queryFn: () => platformApi.listTenants(),
  });
}

export function usePlatformTenant(tenantId: UUID | undefined) {
  return useQuery({
    queryKey: queryKeys.platform.tenant(tenantId ?? ''),
    queryFn: () => platformApi.getTenant(tenantId!),
    enabled: !!tenantId,
  });
}

export function useCreatePlatformTenant() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateTenantInput) => platformApi.createTenant(input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.platform.tenants() });
      void qc.invalidateQueries({ queryKey: queryKeys.platform.dashboard() });
    },
  });
}

export function useUpdatePlatformTenant(tenantId: UUID) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: UpdateTenantInput) => platformApi.updateTenant(tenantId, input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.platform.tenants() });
      void qc.invalidateQueries({ queryKey: queryKeys.platform.tenant(tenantId) });
    },
  });
}

export function useDeletePlatformTenant(tenantId: UUID) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (confirmName: string) => platformApi.deleteTenant(tenantId, confirmName),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.platform.tenants() });
      void qc.invalidateQueries({ queryKey: queryKeys.platform.dashboard() });
      void qc.invalidateQueries({ queryKey: queryKeys.platform.inventory() });
    },
  });
}

export function useDeletePlatformSalesforceOrg(tenantId: UUID) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (orgId: UUID) => platformApi.deleteTenantSalesforceOrg(tenantId, orgId),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.platform.tenant(tenantId) });
      void qc.invalidateQueries({ queryKey: queryKeys.platform.inventory() });
      void qc.invalidateQueries({ queryKey: queryKeys.platform.dashboard() });
    },
  });
}

export function usePlatformInventory() {
  return useQuery({
    queryKey: queryKeys.platform.inventory(),
    queryFn: () => platformApi.getInventory(),
  });
}

export function usePlatformUsers(filters?: { tenantId?: string }) {
  return useQuery({
    queryKey: queryKeys.platform.users(filters),
    queryFn: () => platformApi.listUsers(filters),
  });
}

export function useCreatePlatformUser() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreatePortalUserInput) => platformApi.createUser(input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.platform.users() });
    },
  });
}

export function usePlatformDashboard() {
  return useQuery({
    queryKey: queryKeys.platform.dashboard(),
    queryFn: () => platformApi.getDashboard(),
    refetchInterval: 15_000,
  });
}

export function usePlatformProviders() {
  return useQuery({
    queryKey: queryKeys.platform.providers(),
    queryFn: () => platformApi.listProviders(),
  });
}

export function usePlatformHealth(options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: queryKeys.platform.health(),
    queryFn: () => platformApi.getSystemHealth(),
    enabled: options?.enabled ?? true,
    refetchInterval: options?.enabled === false ? false : 30_000,
  });
}
